import { useState, useCallback, useRef } from "react";
import { Toaster, toast } from "react-hot-toast";
import { toastOptions } from "../common/toastOptions";
import axios from "axios";

function SecureTransfer() {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    [key: string]: number;
  }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles = Array.from(e.dataTransfer.files);
      setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (files.length === 0) {
      toast.error("Please select files to upload");
      return;
    }

    setUploading(true);

    try {
      const uploadPromises = files.map(async (file) => {
        // Initialize progress for this file
        setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));

        // Get pre-signed URL from your backend using POST as in Menu.tsx
        const presignedResponse = await axios.post("/api/get-upload-url", {
          userId: "secure-transfer",
          fileName: file.name,
          fileType: file.type, // Include the file's content type
          bucketName: "police-narratives-ai-secure-file-transfer",
        });

        const { uploadURL: presignedUrl } = presignedResponse.data;

        // Upload to S3 using the pre-signed URL
        await axios.put(presignedUrl, file, {
          headers: {
            "Content-Type": file.type,
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || file.size)
            );
            setUploadProgress((prev) => ({
              ...prev,
              [file.name]: percentCompleted,
            }));
          },
        });

        return file.name;
      });

      await Promise.all(uploadPromises);

      toast.success("All files uploaded successfully!");
      setFiles([]);
      setUploadProgress({});
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Error uploading files. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-base-200">
        <div className="p-8 max-w-6xl mx-auto">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-base-content flex items-center gap-1">
                Secure File Transfer
              </h1>
              <p className="text-gray-600 dark:text-base-content/70 mt-1">
                Transfer files securely to the Police Narratives AI bucket
              </p>
            </div>
          </div>

          {/* File upload area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center ${
              uploading
                ? "border-gray-300 dark:border-base-300 bg-gray-100 dark:bg-base-300"
                : "border-blue-300 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/40 hover:border-blue-400"
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <input
              type="file"
              multiple
              className="hidden"
              ref={fileInputRef}
              onChange={handleFileSelect}
              disabled={uploading}
            />

            <div className="flex flex-col items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 text-blue-500 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <p className="text-lg font-medium mb-2">
                Drag and drop files here, or{" "}
                <button
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200 font-semibold"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  type="button"
                >
                  browse
                </button>
              </p>
              <p className="text-base-content/60 text-sm">
                Upload multiple files to the secure S3 bucket
              </p>
            </div>
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div className="mt-6">
              <h2 className="text-lg font-semibold mb-3">
                Selected Files ({files.length})
              </h2>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {files.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between bg-base-100 p-3 rounded-lg border border-base-300 shadow-sm"
                  >
                    <div className="flex items-center space-x-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-base-content/40"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-base-content/80">
                          {file.name}
                        </p>
                        <p className="text-xs text-base-content/60">
                          {(file.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      {uploadProgress[file.name] > 0 &&
                        uploadProgress[file.name] < 100 && (
                          <div className="w-24">
                            <div className="h-2 bg-base-300 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500 rounded-full"
                                style={{
                                  width: `${uploadProgress[file.name]}%`,
                                }}
                              ></div>
                            </div>
                            <p className="text-xs text-base-content/60 text-right mt-1">
                              {uploadProgress[file.name]}%
                            </p>
                          </div>
                        )}

                      {uploadProgress[file.name] === 100 && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-green-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}

                      {!uploading && uploadProgress[file.name] !== 100 && (
                        <button
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700 dark:hover:text-red-400"
                          type="button"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload button */}
          <div className="mt-6">
            <button
              onClick={uploadFiles}
              disabled={files.length === 0 || uploading}
              className={`px-6 py-3 rounded-lg font-medium text-white ${
                files.length === 0 || uploading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
              type="button"
            >
              {uploading ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Uploading...
                </div>
              ) : (
                "Upload Files"
              )}
            </button>
          </div>
        </div>
      </div>
      <Toaster position="bottom-center" toastOptions={toastOptions} />
    </>
  );
}

export default SecureTransfer;
